import { DEFAULT_DATE_FORMAT } from '@/app-shell/constants';
import { IS_INSET_DESIGN_ENABLED } from '@/app-shell/flags/shell-flags';
import { IS_COMMUNITY_LIKES_ENABLED } from '@/app-shell/flags/templates-flags';
import { useMutateImportTemplateFromGallery } from '@/hooks/mutation/useMutateImportTemplateFromGallery';
import { useQueryCommunityTemplateByTemplateUrlPath } from '@/hooks/query/useQueryCommunityTemplateByTemplateUrlPath';
import { useQueryDocumentTemplates } from '@/hooks/query/useQueryDocumentTemplates';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { usePreserveHashRouter } from '@/hooks/usePreserveHashRouter';
import { isTauri } from '@/tauri/utils';
import { constructPublicAssetUrl } from '@/utils/appConfiguration';
import { logger } from '@/utils/logger/logger';
import { trackButtonClicked } from '@heidi/analytics/events';
import { ButtonAction, ScreenName } from '@heidi/analytics/vocabulary';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { toast } from '@heidi/design-system/toast';
import { TemplateCategory } from '@heidi/schema/template-gallery';
import {
  communityJsonSource,
  communityTemplateDetailsMessages,
  communityXmlSource,
  type FormDemoSubmitSource,
  useQueryCommunityTemplates,
  type XmlFormFillResult,
} from '@heidi/templates';
import type { DetailedPublicTemplateResponse } from '@heidi/templates/contracts';
import { useIsJsonFormEnabled, useIsXmlFormEnabled } from '@heidi/templates/flags';
import { useRouter } from 'next/router';
import {
  createElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

const DATA_URL_PREFIX = 'data:';

const parseDataUrlToBlob = (dataUrl: string): Blob => {
  const commaIndex = dataUrl.indexOf(',');
  if (commaIndex === -1) {
    throw new Error('Invalid data URL');
  }

  const metadata = dataUrl.slice(DATA_URL_PREFIX.length, commaIndex);
  const payload = dataUrl.slice(commaIndex + 1);
  const mimeType = metadata.split(';')[0] || 'application/pdf';
  const isBase64 = metadata.toLowerCase().includes(';base64');

  if (isBase64) {
    const binaryString = atob(payload);
    const bytes = new Uint8Array(binaryString.length);
    for (let index = 0; index < binaryString.length; index += 1) {
      bytes[index] = binaryString.codePointAt(index) ?? 0;
    }
    return new Blob([bytes], { type: mimeType });
  }

  return new Blob([decodeURIComponent(payload)], { type: mimeType });
};

interface UseCommunityTemplateDetailsViewModelResult {
  activePdfUrl: string | null;
  dateFormat: string;
  getUrlWithoutHash: () => string;
  handleBackClick: () => void;
  handleBadgeClick: (type: 'specialty' | 'category') => void;
  handleCopyLinkClick: () => void;
  handleDownloadPdf: () => Promise<void>;
  handleFormDemoSubmit: (source: FormDemoSubmitSource) => void;
  handleFormDemoTrySampleClick: () => void;
  handleOpenFullscreenPreview: () => void;
  handlePostFillSignupClick: () => void;
  handleSaveTemplateAnyway: () => void;
  hasS3ObjectKey: boolean;
  isXmlFormTemplate: boolean;
  xmlSource: string | null;
  xmlFieldValues: Record<string, string> | null;
  setXmlFillResult: (result: XmlFormFillResult | null) => void;
  handleDownloadXml: () => void;
  jsonSource: string | null;
  jsonFieldValues: Record<string, string> | null;
  setJsonFieldValues: (fieldValues: Record<string, string> | null) => void;
  howToUseRichValues: {
    createAction: (chunks: ReactNode) => ReactNode;
    useTemplate: (chunks: ReactNode) => ReactNode;
  };
  isFormTemplate: boolean;
  isLoading: boolean;
  metaDescription: string;
  metaKeywords: string;
  outerShellClassName: string;
  pageTitle: string;
  setDemoFilledPdfUrl: (url: string | null) => void;
  setIsFullscreenPreviewOpen: (isOpen: boolean) => void;
  template: DetailedPublicTemplateResponse | undefined;
  templateAlreadyExists: boolean;
  isAuthenticated: boolean;
  isLikesEnabled: boolean;
  isFullscreenPreviewOpen: boolean;
}

export const useCommunityTemplateDetailsViewModel =
  (): UseCommunityTemplateDetailsViewModelResult => {
    const intl = useIntl();
    const router = useRouter();
    const hashRouter = usePreserveHashRouter();
    const [isFullscreenPreviewOpen, setIsFullscreenPreviewOpen] = useState(false);
    const [demoFilledPdfUrl, setDemoFilledPdfUrl] = useState<string | null>(null);
    const [xmlFill, setXmlFill] = useState<XmlFormFillResult | null>(null);
    const [jsonFieldValues, setJsonFieldValues] = useState<Record<string, string> | null>(
      null
    );

    const urlPath = router.query.urlPath as string;
    const { isAuthenticated } = useIsAuthenticated();

    useEffect(() => {
      setXmlFill(null);
      setJsonFieldValues(null);
    }, [urlPath]);

    const {
      data: template,
      isError,
      isLoading,
    } = useQueryCommunityTemplateByTemplateUrlPath(urlPath);

    const { mutate: importTemplateFromGallery } = useMutateImportTemplateFromGallery();
    const { data: userData } = useQueryUser();
    const { data: userTemplates } = useQueryDocumentTemplates();
    const { refetch } = useQueryCommunityTemplates();

    const templateAlreadyExists = useMemo(() => {
      if (!template?.template_id || !userTemplates) return false;
      return userTemplates.some((userTemplate) =>
        userTemplate.source_template_id?.includes(template.template_id)
      );
    }, [template?.template_id, userTemplates]);

    const howToUseRichValues = useMemo(
      () => ({
        useTemplate: (chunks: ReactNode) => createElement('strong', null, chunks),
        createAction: (chunks: ReactNode) => createElement('strong', null, chunks),
      }),
      []
    );

    const isFormTemplate = useMemo(
      () => template?.template_category === TemplateCategory.FORM_TEMPLATE,
      [template?.template_category]
    );

    const isXmlFormEnabled = useIsXmlFormEnabled();
    const xmlSource = useMemo(
      () => (template ? communityXmlSource(template, isXmlFormEnabled) : null),
      [isXmlFormEnabled, template]
    );

    const isJsonFormEnabled = useIsJsonFormEnabled();
    const jsonSource = useMemo(
      () => (template ? communityJsonSource(template, isJsonFormEnabled) : null),
      [isJsonFormEnabled, template]
    );

    const hasS3ObjectKey = useMemo(
      () => Boolean(template?.template_content?.s3_object_key),
      [template?.template_content?.s3_object_key]
    );

    const pdfUrl = useMemo(() => {
      if (
        !isFormTemplate ||
        !hasS3ObjectKey ||
        !template?.template_content?.s3_object_key
      ) {
        return null;
      }
      return constructPublicAssetUrl(template.template_content.s3_object_key);
    }, [hasS3ObjectKey, isFormTemplate, template?.template_content?.s3_object_key]);

    const activePdfUrl = demoFilledPdfUrl ?? pdfUrl;

    const authenticatedOuterBackgroundClasses = IS_INSET_DESIGN_ENABLED()
      ? 'bg-surface-shell'
      : 'bg-surface-page';

    const outerShellClassName = isAuthenticated
      ? `h-full overflow-auto ${authenticatedOuterBackgroundClasses}`
      : 'h-dvh overflow-auto bg-surface';

    const pageTitle = useMemo(() => {
      if (isLoading) {
        return intl.formatMessage(communityTemplateDetailsMessages.pageTitleLoading);
      }
      if (!template) {
        return intl.formatMessage(communityTemplateDetailsMessages.pageTitleNotFound);
      }
      return intl.formatMessage(communityTemplateDetailsMessages.pageTitleWithTemplate, {
        title: template.title,
        specialty: template.specialty,
        templateKind: intl.formatMessage(
          communityTemplateDetailsMessages.templateKindInTitle
        ),
      });
    }, [intl, isLoading, template]);

    const metaDescription = useMemo(() => {
      if (isLoading || !template) return '';
      const intro = template.description?.trim()
        ? template.description
        : intl.formatMessage(communityTemplateDetailsMessages.metaDescriptionFallback, {
            specialty: template.specialty,
          });
      const author =
        template.author_name ||
        intl.formatMessage(communityTemplateDetailsMessages.defaultAuthorBrand);
      return intl.formatMessage(communityTemplateDetailsMessages.metaDescriptionBody, {
        intro,
        createdBy: intl.formatMessage(communityTemplateDetailsMessages.createdByLabel),
        author,
        trailing: intl.formatMessage(
          communityTemplateDetailsMessages.metaDescriptionTrailing
        ),
      });
    }, [intl, isLoading, template]);

    const metaKeywords = useMemo(() => {
      if (!template) return '';
      return intl.formatMessage(communityTemplateDetailsMessages.metaKeywords, {
        specialty: template.specialty.toLowerCase(),
        category: template.template_category.toLowerCase(),
      });
    }, [intl, template]);

    const getUrlWithoutHash = useCallback(() => {
      if (isTauri()) {
        if (getEnvironment() === ENV_NAME.STAGING) {
          return `https://registrar.scribe.heidihealth.com/templates/${urlPath}`;
        }
        return `https://scribe.heidihealth.com/templates/${urlPath}`;
      }

      if (globalThis.window === undefined) return '';

      return globalThis.location.href.replace(globalThis.location.hash, '');
    }, [urlPath]);

    const handleBadgeClick = useCallback(
      (type: 'specialty' | 'category') => {
        const query: Record<string, string> = {};

        if (template?.specialty) {
          query.specialty = template.specialty;
        }

        if (type === 'category' && template?.template_category) {
          query.category = template.template_category;
        }

        void router.push({ pathname: '/templates', query });
      },
      [router, template?.specialty, template?.template_category]
    );

    const handleBackClick = useCallback(() => {
      router.back();
    }, [router]);

    const handlePostFillSignupClick = useCallback(() => {
      trackButtonClicked({
        screenName: ScreenName.TemplateCommunityDetails,
        action: ButtonAction.CommunityTemplateFormDemoSignupClick,
        label: 'Sign up to save filled form',
      });
      void router.push(
        `/onboarding?post_login_redirect_url=${encodeURIComponent(
          '/scribe/my-templates/community-import'
        )}`
      );
    }, [router]);

    const handleFormDemoTrySampleClick = useCallback(() => {
      trackButtonClicked({
        screenName: ScreenName.TemplateCommunityDetails,
        action: ButtonAction.CommunityTemplateFormDemoTrySampleClick,
        label: 'Use sample',
      });
    }, []);

    const handleFormDemoSubmit = useCallback((source: FormDemoSubmitSource) => {
      trackButtonClicked({
        screenName: ScreenName.TemplateCommunityDetails,
        action: ButtonAction.CommunityTemplateFormDemoSubmit,
        label: 'Fill form',
        clickSource: source,
      });
    }, []);

    const handleOpenFullscreenPreview = useCallback(() => {
      trackButtonClicked({
        screenName: ScreenName.TemplateCommunityDetails,
        action: ButtonAction.FormPreviewFullscreen,
        label: 'Form Preview Fullscreen',
      });
      setIsFullscreenPreviewOpen(true);
    }, []);

    const handleDownloadPdf = useCallback(async () => {
      const downloadUrl = demoFilledPdfUrl ?? pdfUrl;
      if (!downloadUrl || !template) {
        return;
      }

      try {
        let blob: Blob;
        if (downloadUrl.startsWith(DATA_URL_PREFIX)) {
          blob = parseDataUrlToBlob(downloadUrl);
        } else {
          const response = await fetch(downloadUrl);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          blob = await response.blob();
        }

        const url = globalThis.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const filenameSuffix = demoFilledPdfUrl ? '_filled' : '';
        link.download = `${template.title
          .replaceAll(/[^a-z0-9]/gi, '_')
          .toLowerCase()}${filenameSuffix}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        globalThis.URL.revokeObjectURL(url);
        trackButtonClicked({
          screenName: ScreenName.TemplateCommunityDetails,
          action: ButtonAction.CommunityTemplateFormDemoDownload,
          label: 'Download PDF',
          clickSource: demoFilledPdfUrl ? 'filled' : 'unfilled',
        });
      } catch (error) {
        logger.error('Error downloading PDF:', error);
        toast({
          tone: 'destructive',
          title: intl.formatMessage(communityTemplateDetailsMessages.pdfDownloadError),
        });
      }
    }, [demoFilledPdfUrl, intl, pdfUrl, template]);

    const setXmlFillResult = useCallback((result: XmlFormFillResult | null) => {
      setXmlFill(result);
    }, []);

    const handleDownloadXml = useCallback(() => {
      const source = xmlFill?.filledXml ?? xmlSource;
      if (!source || !template) {
        return;
      }

      try {
        const blob = new Blob([source], { type: 'application/xml' });
        const url = globalThis.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const filenameSuffix = xmlFill ? '_filled' : '';
        link.download = `${template.title
          .replaceAll(/[^a-z0-9]/gi, '_')
          .toLowerCase()}${filenameSuffix}.xml`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        globalThis.URL.revokeObjectURL(url);
        trackButtonClicked({
          screenName: ScreenName.TemplateCommunityDetails,
          action: ButtonAction.CommunityTemplateFormDemoDownload,
          label: 'Download XML',
          clickSource: xmlFill ? 'filled' : 'unfilled',
        });
      } catch (error) {
        logger.error('Error downloading XML form', {
          error_type: error instanceof Error ? error.name : 'unknown',
        });
        toast({
          tone: 'destructive',
          title: intl.formatMessage(communityTemplateDetailsMessages.xmlDownloadError),
        });
      }
    }, [intl, template, xmlFill, xmlSource]);

    const handleCopyLinkClick = useCallback(() => {
      const shareUrl = getUrlWithoutHash();
      if (!shareUrl) return;

      void navigator.clipboard.writeText(shareUrl);
      toast({
        tone: 'success',
        title: intl.formatMessage(communityTemplateDetailsMessages.linkCopiedToast),
      });
    }, [getUrlWithoutHash, intl]);

    const handleSaveTemplateAnyway = useCallback(() => {
      if (template?.template_id) {
        importTemplateFromGallery({
          input: { templateId: template.template_id },
          saveAnyway: true,
        });
      }
    }, [importTemplateFromGallery, template?.template_id]);

    useEffect(() => {
      if (isError) {
        void (async () => {
          hashRouter.push('/templates');
          await refetch();
          toast({
            title: intl.formatMessage(
              communityTemplateDetailsMessages.templateNotFoundToast
            ),
            tone: 'destructive',
          });
        })();
      }
    }, [hashRouter, intl, isError, refetch]);

    return {
      activePdfUrl,
      dateFormat: userData?.session_settings?.date_format ?? DEFAULT_DATE_FORMAT,
      getUrlWithoutHash,
      handleBackClick,
      handleBadgeClick,
      handleCopyLinkClick,
      handleDownloadPdf,
      handleFormDemoSubmit,
      handleFormDemoTrySampleClick,
      handleOpenFullscreenPreview,
      handlePostFillSignupClick,
      handleSaveTemplateAnyway,
      hasS3ObjectKey,
      handleDownloadXml,
      howToUseRichValues,
      isFormTemplate,
      isXmlFormTemplate: xmlSource !== null,
      setXmlFillResult,
      xmlFieldValues: xmlFill?.fieldValues ?? null,
      xmlSource,
      jsonSource,
      jsonFieldValues,
      setJsonFieldValues,
      isLikesEnabled: IS_COMMUNITY_LIKES_ENABLED(),
      isLoading,
      isFullscreenPreviewOpen,
      metaDescription,
      metaKeywords,
      outerShellClassName,
      pageTitle,
      setDemoFilledPdfUrl,
      setIsFullscreenPreviewOpen,
      template,
      templateAlreadyExists,
      isAuthenticated,
    };
  };
